"use client";
import React, { useEffect, useState } from "react";
import { clsx } from "clsx";
import { m } from "motion/react";
import Image from "next/image";
import {
  StrategyVisual,
  TrafficVisual,
  RetentionVisual,
  BrandingVisual,
  TechVisual
} from "@/components/bento-visuals";

export default function FUIBentoGridDark() {
  // Cards – Midas Operations (Ambition & Dominance Tone)
  const cards = [
    {
      eyebrow: "O Plano Mestre",
      title: "Dominação de Mercado",
      description:
        "Não jogamos para competir, jogamos para ganhar. O mapa exato para tornar sua concorrência irrelevante.",
      visual: <StrategyVisual />,
    },
    {
      eyebrow: "Injeção de Capital",
      title: "Escala Agressiva",
      description:
        "Transformamos tráfego em lucro líquido. Sem métricas de vaidade, apenas ROI brutal e aquisição em massa.",
      visual: <TrafficVisual />,
    },
    {
      eyebrow: "LTV Máximo",
      title: "Máquina de Vendas",
      description:
        "Enquanto eles dormem, você lucra. Automação implacável para extrair cada centavo de valor da sua base.",
      visual: <RetentionVisual />,
    },
    {
      eyebrow: "Legado",
      title: "Marca Intocável",
      description:
        "Seja a única opção lógica. Construímos a autoridade que permite cobrar mais caro e ser amado por isso.",
      visual: <BrandingVisual />,
    },
    {
      eyebrow: "Alicerces",
      title: "Infraestrutura de Elite",
      description:
        "Velocidade é dinheiro. Um ecossistema digital blindado, pronto para suportar milhões em vendas sem falhar.",
      visual: <TechVisual />,
    },
  ];

  return (
    <div id="servicos" className="container py-16 mx-auto px-4">
      <div className="grid grid-cols-1 gap-4 sm:mt-16 lg:grid-cols-6 lg:grid-rows-2">
        <BentoCard
          eyebrow={cards[0].eyebrow}
          title={cards[0].title}
          description={cards[0].description}
          visual={cards[0].visual}
          className="max-lg:rounded-t-4xl lg:col-span-3 lg:rounded-tl-4xl"
        />
        <BentoCard
          eyebrow={cards[1].eyebrow}
          title={cards[1].title}
          description={cards[1].description}
          visual={cards[1].visual}
          className="lg:col-span-3 lg:rounded-tr-4xl"
        />
        <BentoCard
          eyebrow={cards[2].eyebrow}
          title={cards[2].title}
          description={cards[2].description}
          visual={cards[2].visual}
          className="lg:col-span-2 lg:rounded-bl-4xl"
        />
        <BentoCard
          eyebrow={cards[3].eyebrow}
          title={cards[3].title}
          description={cards[3].description}
          visual={cards[3].visual}
          className="lg:col-span-2"
        />
        <BentoCard
          eyebrow={cards[4].eyebrow}
          title={cards[4].title}
          description={cards[4].description}
          visual={cards[4].visual}
          className="max-lg:rounded-b-4xl lg:col-span-2 lg:rounded-br-4xl rounded-3xl"
        />
      </div>
    </div>
  );
}

export function BentoCard({
  dark = false,
  className = "",
  eyebrow,
  title,
  description,
  imageUrl,
  visual,
  imagePosition = "",
  fade = [],
}: {
  dark?: boolean;
  className?: string;
  eyebrow: React.ReactNode;
  title: string;
  description: React.ReactNode;
  imageUrl?: string;
  visual?: React.ReactNode;
  imagePosition?: string;
  fade?: ("top" | "bottom")[];
}) {
  const [isMobileLocal, setIsMobileLocal] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobileLocal(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
    <m.div
      initial="idle"
      whileHover="active"
      variants={{
        idle: { scale: 1 },
        active: { scale: 1.03 } // Increased scale for more aggression
      }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      data-dark={dark ? "true" : undefined}
      className={clsx(
        className,
        "group relative flex flex-col overflow-hidden rounded-lg",
        "bg-transparent transform-gpu dark:[border:1px_solid_rgba(255,255,255,.1)] dark:[box-shadow:0_-20px_80px_-20px_#8686f01f_inset] shadow-sm ring-1 ring-black/5",
        "data-[dark]:bg-gray-800 data-[dark]:ring-white/15",
        "hover:ring-midas-yellow-500/50 hover:shadow-[0_0_50px_-10px_rgba(255,205,0,0.5)] transition-all duration-300" // Intensified shadow
      )}
    >
      <div className="relative h-[29rem] shrink-0 overflow-hidden bg-neutral-900">
        {visual ? (
          <div className="absolute inset-0 z-0">
            {visual}
          </div>
        ) : imageUrl ? (
          <div className={`absolute inset-0 ${imagePosition}`}>
            <Image
              src={imageUrl}
              alt={title}
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              style={{ objectFit: "cover" }}
            />
          </div>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-neutral-800 to-black">
            <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-20 mix-blend-overlay" />
            {/* Mid-card accent */}
            <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-midas-yellow-500/10 rounded-full blur-[80px]" />
          </div>
        )}
        {fade.includes("top") && (
          <div className="absolute inset-0 bg-gradient-to-b from-white to-50% group-data-[dark]:from-gray-800 group-data-[dark]:from-[-25%] opacity-25 z-10" />
        )}
        {fade.includes("bottom") && (
          <div className="absolute inset-0 bg-gradient-to-t from-white to-50% group-data-[dark]:from-gray-800 group-data-[dark]:from-[-25%] opacity-25 z-10" />
        )}
      </div>
      <div className={clsx(
        "relative p-10 z-20 isolate mt-[-110px] h-fit",
        isMobileLocal ? "bg-gradient-to-t from-black/100 via-black/90 to-black/30" : "backdrop-blur-3xl"
      )}>
        <h1 className="uppercase tracking-widest text-sm font-semibold text-midas-yellow-500 mb-2">{eyebrow}</h1>
        <p className="mt-1 text-2xl/8 font-bold tracking-tight dark:text-gray-100 text-gray-950 group-data-[dark]:text-white">
          {title}
        </p>
        <p className="mt-2 max-w-[600px] text-sm/6 text-gray-400 group-data-[dark]:text-gray-400">
          {description}
        </p>
      </div>
    </m.div>
  );
}
